import React, { useState, useEffect } from 'react';
import { Card, Button, Form } from 'react-bootstrap';

function RestBlockCard({ restBlock, onUpdate, onDelete, selected, onToggleSelect }) {
  const [duration, setDuration] = useState(restBlock.rest_duration_seconds || 90);
  const [notes, setNotes] = useState(restBlock.rest_notes || '');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(duration);

  // Timer countdown effect
  useEffect(() => {
    let interval;
    if (isTimerRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(t => {
          if (t <= 1) {
            setIsTimerRunning(false);
            // Timer complete - could add sound/notification here
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timeRemaining]);

  // Update timeRemaining when duration changes (only when timer is not running)
  useEffect(() => {
    if (!isTimerRunning) {
      setTimeRemaining(duration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]); // Only trigger when duration changes, not when isTimerRunning changes

  // Update parent component when duration or notes change
  useEffect(() => {
    onUpdate({
      rest_duration_seconds: duration,
      rest_notes: notes
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, notes]);

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = () => {
    setTimeRemaining(duration);
    setIsTimerRunning(true);
  };

  const handlePauseTimer = () => {
    setIsTimerRunning(false);
  };

  const handleResetTimer = () => {
    setIsTimerRunning(false);
    setTimeRemaining(duration);
  };

  return (
    <Card className="rest-block-card">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          {onToggleSelect && (
            <Form.Check
              type="checkbox"
              className="me-2"
              checked={!!selected}
              onChange={onToggleSelect}
              title="Select for superset"
            />
          )}
          <span className="drag-handle me-2">
            <i className="fas fa-grip-vertical"></i>
          </span>
          <h6 className="mb-0">
            <i className="fas fa-pause-circle me-2"></i>
            Rest Block
          </h6>
        </div>
        {onDelete && (
          <Button
            variant="outline-dark"
            size="sm"
            onClick={onDelete}
          >
            <i className="fas fa-trash"></i>
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        <div className="row">
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Rest Duration (seconds)</Form.Label>
              <Form.Control
                type="number"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                disabled={isTimerRunning}
                min="0"
              />
              <Form.Text className="text-muted">
                {formatTime(duration)}
              </Form.Text>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Water break, stretch"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Form.Group>
          </div>
        </div>

        {/* Timer Display */}
        <div className="text-center mt-3">
          <div className={`rest-timer-display ${isTimerRunning ? 'active' : ''}`}>
            <h2 className="mb-2">{formatTime(timeRemaining)}</h2>
            <div className="btn-group">
              {!isTimerRunning && timeRemaining === duration ? (
                // Initial state - show Start button
                <Button
                  variant="success"
                  onClick={handleStartTimer}
                  disabled={duration === 0}
                >
                  <i className="fas fa-play me-1"></i> Start Timer
                </Button>
              ) : !isTimerRunning && timeRemaining !== duration ? (
                // Paused state - show Resume and Reset buttons
                <>
                  <Button
                    variant="success"
                    onClick={() => setIsTimerRunning(true)}
                    className="me-2"
                  >
                    <i className="fas fa-play me-1"></i> Resume
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleResetTimer}
                  >
                    <i className="fas fa-redo me-1"></i> Reset
                  </Button>
                </>
              ) : (
                // Running state - show Pause button
                <Button
                  variant="warning"
                  onClick={handlePauseTimer}
                >
                  <i className="fas fa-pause me-1"></i> Pause
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default RestBlockCard;
